import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { ADS_CAMPAIGN_SEARCH_MAX_Q, ADS_CAMPAIGN_SEARCH_DEFAULT_LIMIT, ADS_CAMPAIGN_SEARCH_MAX_IDS, } from '@wit-support/admin-contracts';
import { useDebouncedValue } from '@wit-support/ui';
import { CampaignPicker, isRawCampaignId, looksLikeUrl, } from '@wit-support/ui-domain';
import { searchCampaigns } from './api.js';
import { ADS_QUERY_DEFAULTS, REFERENCE_STALE_MS } from './query-policy.js';
import { describeAdsError } from './shared.js';
/*
 * Кампании Meta для пикера (`GET /api/ads/campaigns` — поиск по базе
 * auto-ads, Meta не вызывается). Два запроса:
 * - поиск под ввод: пауза 300 мс после последней буквы, пустой ввод — свежие
 *   / активные кампании выбранных кабинетов; минута свежести; старая выдача
 *   остаётся на экране, пока идёт новая (`keepPreviousData`);
 * - подписи выбранных (`ids=`): ВСЕ выбранные одним запросом (по 50 в пачке),
 *   только тех, кого экран ещё не видел в выдаче поиска.
 * Ключи — НЕ под `ADS_KEY`: мутации раздела сбрасывают `['ads']`, а
 * кампании от них не меняются.
 */
export const CAMPAIGNS_KEY = ['ads-campaigns'];
/** Поиск: выдача меняется (новые кампании, статусы) — минута. */
export const CAMPAIGN_SEARCH_STALE_MS = 60_000;
export const CAMPAIGN_SEARCH_DEBOUNCE_MS = 300;
const ID_RE = /^\d{1,30}$/;
/** Строка ответа → строка пикера (статус — `effective_status`, иначе `status`). */
export function toCampaignPickerOption(c) {
    return {
        campaignId: c.campaignId,
        name: c.name,
        accountName: c.accountName,
        platformAccountId: c.platformAccountId,
        status: c.effectiveStatus ?? c.status,
    };
}
const uniqSorted = (xs) => [...new Set((xs ?? []).filter((x) => x !== ''))].sort();
/** Пачки id для `ids=` (≤ 50 — предел admin-api/auto-ads). */
export function chunkCampaignIds(ids, size = ADS_CAMPAIGN_SEARCH_MAX_IDS) {
    const out = [];
    for (let i = 0; i < ids.length; i += size)
        out.push(ids.slice(i, i + size));
    return out;
}
export const campaignSearchKey = (accounts, q, statuses) => [...CAMPAIGNS_KEY, 'search', { accounts, q, statuses }];
/** Поиск кампаний под ввод (ввод уже отложен вызывающим). */
export function useCampaignSearch({ platformAccountIds, statuses, q, enabled = true, }) {
    const accounts = useMemo(() => uniqSorted(platformAccountIds), [platformAccountIds]);
    const sts = useMemo(() => uniqSorted(statuses), [statuses]);
    // длиннее предела auto-ads ответит 400 — режем (имена кампаний короче)
    const query = q.trim().slice(0, ADS_CAMPAIGN_SEARCH_MAX_Q);
    return useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: campaignSearchKey(accounts, query, sts),
        queryFn: () => searchCampaigns({
            platformAccountIds: accounts,
            statuses: sts,
            q: query,
            limit: ADS_CAMPAIGN_SEARCH_DEFAULT_LIMIT,
        }),
        staleTime: CAMPAIGN_SEARCH_STALE_MS,
        placeholderData: keepPreviousData,
        enabled,
    });
}
/**
 * Подписи кампаний по id: ОДИН запрос `ids=` на все (больше 50 — пачками),
 * без фильтра кабинета (выбранная кампания может быть из другого). Нет в
 * ответе — кампании нет в нашей базе.
 */
export function useCampaignsByIds(ids) {
    const norm = useMemo(() => uniqSorted(ids.filter((x) => ID_RE.test(x))), [ids]);
    const results = useQueries({
        queries: chunkCampaignIds(norm).map((chunk) => ({
            ...ADS_QUERY_DEFAULTS,
            queryKey: [...CAMPAIGNS_KEY, 'ids', chunk.join(',')],
            queryFn: () => searchCampaigns({ ids: chunk, limit: chunk.length }),
            staleTime: REFERENCE_STALE_MS,
        })),
    });
    const stamp = results.map((r) => r.dataUpdatedAt).join(',');
    const options = useMemo(() => results.flatMap((r) => r.data?.items ?? []).map(toCampaignPickerOption), 
    // results — новый массив каждый рендер; данные меняются только вместе со stamp
    [stamp]);
    return {
        options,
        isLoading: results.some((r) => r.isLoading),
        isError: results.some((r) => r.isError),
    };
}
/**
 * Пикер кампаний раздела: `CampaignPicker` (`ui-domain`) + данные. Поиск
 * начинается с первого открытия списка; подписи выбранных — сразу.
 */
export function AdsCampaignPicker(props) {
    const { platformAccountIds, statuses, ...rest } = props;
    const [query, setQuery] = useState('');
    const [armed, setArmed] = useState(false);
    const debounced = useDebouncedValue(query, CAMPAIGN_SEARCH_DEBOUNCE_MS);
    const typed = debounced.trim();
    // ссылка без id кампании — искать по её тексту бессмысленно (пикер объясняет)
    const searchable = !looksLikeUrl(typed);
    const search = useCampaignSearch({
        platformAccountIds,
        statuses,
        q: typed,
        enabled: armed && searchable,
    });
    /** Кампании, которые экран уже видел (выдача поиска, выбор): их подписи не переспрашиваем. */
    const [seen, setSeen] = useState(new Map());
    const searchOptions = useMemo(() => (search.data?.items ?? []).map(toCampaignPickerOption), [search.data]);
    useEffect(() => {
        if (searchOptions.length === 0)
            return;
        setSeen((cur) => {
            if (searchOptions.every((o) => cur.has(o.campaignId)))
                return cur;
            const next = new Map(cur);
            for (const o of searchOptions)
                next.set(o.campaignId, o);
            return next;
        });
    }, [searchOptions]);
    const values = useMemo(() => (props.multiple ? props.value : props.value ? [props.value] : []), [props.multiple, props.value]);
    const missing = useMemo(() => values.filter((v) => !seen.has(v) && !searchOptions.some((o) => o.campaignId === v)), [values, seen, searchOptions]);
    const byIds = useCampaignsByIds(missing);
    // Целый id, которого нет в выдаче (другой кабинет, отфильтрованный статус):
    // точный `ids=` без фильтров — это кампания из нашей базы, а не «не найдена».
    const lookupIds = useMemo(() => armed &&
        isRawCampaignId(typed) &&
        search.data !== undefined &&
        !search.isPlaceholderData &&
        !search.data.items.some((c) => c.campaignId === typed)
        ? [typed]
        : [], [armed, typed, search.data, search.isPlaceholderData]);
    const lookup = useCampaignsByIds(lookupIds);
    const selectedOptions = useMemo(() => {
        const m = new Map();
        for (const o of byIds.options)
            m.set(o.campaignId, o);
        for (const v of values) {
            const o = seen.get(v) ?? searchOptions.find((x) => x.campaignId === v);
            if (o)
                m.set(v, o);
        }
        return [...m.values()].filter((o) => values.includes(o.campaignId));
    }, [byIds.options, values, seen, searchOptions]);
    const remember = useCallback((opts) => {
        // id без подписи (вставлен руками) — не «видели»: его спросит `ids=`
        const known = opts.filter((o) => o.name !== null || o.accountName !== null);
        if (known.length === 0)
            return;
        setSeen((cur) => {
            const next = new Map(cur);
            for (const o of known)
                next.set(o.campaignId, o);
            return next;
        });
    }, []);
    const common = {
        options: armed ? searchOptions : [],
        selectedOptions,
        knownOptions: lookup.options,
        ...(platformAccountIds !== undefined ? { platformAccountIds } : {}),
        query,
        onQueryChange: setQuery,
        // идёт запрос или ввод ещё не ушёл (пауза 300 мс) — выдача на экране старая
        loading: armed &&
            (search.isFetching ||
                lookup.isLoading ||
                query.trim() !== typed ||
                (isRawCampaignId(typed) && searchable && search.isPlaceholderData)),
        resolving: byIds.isLoading,
        error: search.isError && !search.data ? describeAdsError(search.error) : null,
        onOpenChange: (open) => {
            if (open)
                setArmed(true);
        },
        ...(rest.placeholder !== undefined ? { placeholder: rest.placeholder } : {}),
        ...(rest.disabled !== undefined ? { disabled: rest.disabled } : {}),
        ...(rest.allowRawId !== undefined ? { allowRawId: rest.allowRawId } : {}),
        ...(rest.rawIdHint !== undefined ? { rawIdHint: rest.rawIdHint } : {}),
        ...(rest.id !== undefined ? { id: rest.id } : {}),
        ...(rest['aria-label'] !== undefined ? { 'aria-label': rest['aria-label'] } : {}),
        ...(rest['aria-describedby'] !== undefined
            ? { 'aria-describedby': rest['aria-describedby'] }
            : {}),
        ...(rest['aria-invalid'] !== undefined ? { 'aria-invalid': rest['aria-invalid'] } : {}),
        ...(rest.className !== undefined ? { className: rest.className } : {}),
    };
    if (props.multiple) {
        const { onChange } = props;
        return (_jsx(CampaignPicker, { ...common, multiple: true, value: props.value, ...(props.max !== undefined ? { max: props.max } : {}), onChange: (ids, opts) => {
                remember(opts);
                onChange(ids, opts);
            } }));
    }
    const { onChange } = props;
    return (_jsx(CampaignPicker, { ...common, value: props.value, onChange: (id, opt) => {
            if (opt)
                remember([opt]);
            onChange(id, opt);
        } }));
}
//# sourceMappingURL=campaigns.js.map